import React, { useState, useEffect, useCallback } from "react";
import Highcharts from "highcharts";
import ResponsiveHighchartsReact from "../common/ResponsiveHighchartsReact";
import { capacityAPI } from "../../services/api";
import { getFuelColor } from "../../utils/fuelColors";
import { createAreaChartConfig } from "../../utils/highchartsConfig";
import { smartFormatNumber } from "../../utils/numberFormatting";
import toast from "react-hot-toast";

const CapacityChart = ({ countryIso, year = 2022, height }) => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadCapacityData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await capacityAPI.getCapacityUtilization(
        countryIso,
        year
      );

      // Data is now returned directly from backend (PostgreSQL structure)
      if (
        response &&
        response.success &&
        response.data
      ) {
        setChartData(response.data);
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      console.error("Error loading capacity data:", error);
      setError(error.message);
      toast.error("Failed to load capacity data");
    } finally {
      setLoading(false);
    }
  }, [countryIso, year]);

  useEffect(() => {
    loadCapacityData();
  }, [loadCapacityData]);

  const getChartOptions = () => {
    if (!chartData || !chartData.capacity_chart) {
      console.warn("CapacityChart: Missing required data properties");
      // Return a basic chart structure even with no data
      return createAreaChartConfig({
        title: "No Capacity Data Available",
        subtitle: "Installed capacity by fuel type (2000-2025)",
        xAxis: { categories: [], title: '' },
        yAxis: { title: "GW" },
        series: [],
        tooltip: { shared: true },
        plotOptions: { stacking: 'normal' }
      });
    }

    // Check if capacity_chart has actual data (not empty object)
    const hasCapacityData =
      chartData.capacity_chart &&
      Object.keys(chartData.capacity_chart).length > 0 &&
      Object.values(chartData.capacity_chart).some(
        (arr) => arr && arr.length > 0
      );

    if (!hasCapacityData) {
      console.warn(
        "CapacityChart: No capacity data available for this country"
      );
      // Return a basic chart structure with no data message
      return createAreaChartConfig({
        title: "No Capacity Data Available",
        subtitle: "Installed capacity by fuel type (2000-2025)",
        xAxis: { categories: [], title: '' },
        yAxis: { title: "GW" },
        series: [],
        tooltip: { shared: true },
        plotOptions: { stacking: 'normal' }
      });
    }

    const { capacity_chart, fuel_types, years } = chartData;

    // Use fuel_types and years from backend, fallback to extracting from capacity_chart
    const fuelTypes = fuel_types || Object.keys(capacity_chart);
    const chartYears = years || Array.from({ length: capacity_chart[fuelTypes[0]]?.length || 0 }, (_, i) => 2000 + i);

    // Create series data for each fuel type
    const series = fuelTypes.map((fuelType) => {
      const fuelData = capacity_chart[fuelType] || [];
      // Convert string values to numbers for Highcharts
      const numericData = fuelData.map(val => {
        const num = parseFloat(val);
        return isNaN(num) ? 0 : num;
      });
      return {
        name: fuelType.charAt(0).toUpperCase() + fuelType.slice(1),
        data: numericData,
        type: "area",
        stacking: "normal",
        color: getFuelColor(fuelType),
      };
    });

    return createAreaChartConfig({
      title: "Capacity Evolution",
      subtitle: "Installed capacity by fuel type (2000-2025)",
      height: height || 400,
      xAxis: { categories: chartYears, title: '' },
      yAxis: { title: "GW" },
      series,
      tooltip: {
        shared: true,
        formatter: function () {
          let tooltip = `<b>${this.x}</b><br/>`;
          this.points.forEach((point) => {
            if (point.y > 0) {
              const formattedValue = smartFormatNumber(point.y);
              tooltip += `<span style="color:${point.color}">●</span> ${
                point.series.name
              }: <b>${formattedValue} GW</b><br/>`;
            }
          });
          return tooltip;
        }
      },
      plotOptions: {
        stacking: 'normal'
      },
      showLegend: false, // Hide legend since labels are on areas
      enableSeriesLabels: true // Enable series-label module
    });
  };

  if (loading) {
    return (
      <div className="w-full min-w-0 px-0 lg:px-4 py-2 lg:py-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-56 md:h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-72 md:h-80 lg:h-96 bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <p className="text-gray-900 text-sm md:text-base lg:text-lg font-medium mb-2">Failed to load data</p>
          <p className="text-gray-600 text-sm md:text-base mb-4">{error}</p>
          <button onClick={loadCapacityData} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!chartData) {
    return (
      <div className="flex items-center justify-center h-72 md:h-80 lg:h-96 bg-gray-50 rounded-lg">
        <div className="text-center">
          <p className="text-gray-600 text-sm md:text-base">No capacity data available</p>
        </div>
      </div>
    );
  }

  // Always render the chart container, let Highcharts handle empty data
  return (
    <div className="w-full min-w-0 overflow-x-auto px-0 lg:px-4 py-2 lg:py-4">
      <ResponsiveHighchartsReact highcharts={Highcharts} options={getChartOptions()} />
    </div>
  );
};

export default CapacityChart;